import { motion } from "framer-motion";
import { Mail, MapPin, MessageCircle, Navigation, Phone } from "lucide-react";
import { trackClick } from "@/lib/api";
import { useContact } from "@/lib/contact";

const EASE = [0.22, 1, 0.36, 1];

const contactRow =
    "group flex items-center gap-3.5 text-sm font-semibold text-ink/70 transition-colors hover:text-ink";

const iconChip =
    "flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-purple/10 via-brand-blue/10 to-brand-teal/10 transition-transform duration-300 group-hover:-translate-y-0.5";

export default function LocationSection() {
    const contact = useContact();

    return (
        <section id="location" data-testid="location-section" className="cv-auto relative overflow-hidden py-20 sm:py-28">
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full opacity-[0.05]"
                style={{ background: "radial-gradient(circle, #22B3A3 0%, transparent 65%)" }}
            />

            <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-[44%_56%] lg:gap-14">
                {/* Content */}
                <motion.div
                    initial={{ opacity: 0, y: 22 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.75, ease: EASE }}
                >
                    <p
                        data-testid="location-eyebrow"
                        className="inline-flex items-center gap-2.5 rounded-full border border-brand-purple/15 bg-white/70 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.24em] text-ink/60"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-brand-purple to-brand-teal" />
                        Visit Us
                    </p>
                    <h2 className="mt-6 text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl">
                        In the Heart of <span className="text-gradient">Bikaner.</span>
                    </h2>
                    <p className="mt-4 max-w-md text-sm font-medium leading-relaxed text-ink/55 md:text-base">
                        Find us near Murti Circle — easy to reach, with free parking on the premises.
                    </p>

                    <div className="mt-8 space-y-4">
                        <div className="flex items-start gap-3.5" data-testid="location-address">
                            <span className={`${iconChip} text-brand-purple`}>
                                <MapPin size={17} strokeWidth={2.2} />
                            </span>
                            <address className="pt-1 text-sm font-semibold not-italic leading-relaxed text-ink/70">
                                {contact.name}, {contact.address.includes(", Near") ? `${contact.address.split(", Near")[0]},` : contact.address}
                                {contact.address.includes(", Near") && (
                                    <>
                                        <br />
                                        Near{contact.address.split(", Near")[1]}
                                    </>
                                )}
                            </address>
                        </div>
                        <a data-testid="location-phone-link" href={contact.telUrl} onClick={() => trackClick("call", "location")} className={contactRow}>
                            <span className={`${iconChip} text-brand-blue`}>
                                <Phone size={16} strokeWidth={2.2} />
                            </span>
                            {contact.phoneDisplay}
                        </a>
                        <a data-testid="location-email-link" href={contact.mailtoUrl} className={contactRow}>
                            <span className={`${iconChip} text-brand-teal`}>
                                <Mail size={16} strokeWidth={2.2} />
                            </span>
                            {contact.email}
                        </a>
                    </div>

                    <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                        <a
                            data-testid="location-directions-btn"
                            href={contact.googleMapsDirectionsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-gradient group inline-flex w-full items-center justify-center gap-2.5 rounded-full px-7 py-4 text-[15px] font-bold text-white sm:w-auto"
                        >
                            <Navigation size={16} strokeWidth={2.4} />
                            Get Directions
                        </a>
                        <a
                            data-testid="location-call-btn"
                            href={contact.telUrl}
                            onClick={() => trackClick("call", "location")}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-300/90 bg-white/70 px-6 py-4 text-sm font-bold text-ink transition-all duration-300 hover:-translate-y-0.5 hover:border-ink/30 active:scale-[0.98] sm:w-auto"
                        >
                            <Phone size={15} strokeWidth={2.4} className="text-brand-blue" />
                            Call Now
                        </a>
                        <a
                            data-testid="location-whatsapp-btn"
                            href={contact.whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackClick("whatsapp", "location")}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-300/90 bg-white/70 px-6 py-4 text-sm font-bold text-ink transition-all duration-300 hover:-translate-y-0.5 hover:border-ink/30 active:scale-[0.98] sm:w-auto"
                        >
                            <MessageCircle size={15} strokeWidth={2.4} className="text-brand-teal" />
                            WhatsApp
                        </a>
                    </div>
                </motion.div>

                {/* Map */}
                <motion.div
                    initial={{ clipPath: "inset(0 0 100% 0)", opacity: 0.4 }}
                    whileInView={{ clipPath: "inset(0 0 0% 0)", opacity: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.85, ease: EASE, delay: 0.1 }}
                    className="relative"
                >
                    <div
                        data-testid="location-map"
                        className="group relative overflow-hidden rounded-[2rem] border border-slate-200/80 bg-white shadow-[0_36px_80px_-45px_rgba(22,50,79,0.35)]"
                    >
                        {/* real Google Map (keyless embed) — clicks pass to the overlay link */}
                        <div className="relative h-[340px] transition-transform duration-700 ease-out group-hover:scale-[1.015] sm:h-[420px]">
                            <iframe
                                title="Work Island location map"
                                src={contact.mapEmbedUrl}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                className="pointer-events-none absolute inset-0 h-full w-full border-0"
                            />
                            {/* click-through overlay → exact Work Island destination */}
                            <a
                                data-testid="location-map-link"
                                href={contact.googleMapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Open Work Island location in Google Maps"
                                className="absolute inset-0"
                            >
                                <span className="pointer-events-none absolute inset-x-0 bottom-0 flex translate-y-2 items-center justify-center bg-gradient-to-t from-ink/70 via-ink/25 to-transparent p-5 pt-12 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                                    <span className="rounded-full bg-white/95 px-4 py-2 text-[12.5px] font-bold text-ink shadow-md">
                                        Open in Google Maps →
                                    </span>
                                </span>
                            </a>
                        </div>

                        {/* location chip */}
                        <div className="pointer-events-none absolute left-4 top-4 flex items-center gap-2.5 rounded-2xl border border-slate-200/70 bg-white/95 px-4 py-3 shadow-md backdrop-blur-sm">
                            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-brand-purple via-brand-blue to-brand-teal text-white">
                                <MapPin size={15} strokeWidth={2.4} />
                            </span>
                            <span>
                                <span className="block text-[13px] font-bold leading-tight text-ink">
                                    {contact.name}
                                </span>
                                <span className="block text-[11px] font-semibold text-ink/50">
                                    {contact.shortAddress}
                                </span>
                            </span>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
